import React, { useCallback, useEffect, useState } from 'react';
import { healthsherpa } from './api';
import { usePersonColors } from './HealthSherpaPerson';
import { fs } from './hsScale';
import { smsSegments } from './hsSms';

// =============================================================================
// CMS consumer consent by text (5.40.0, Henry 2026-11-01). The CMS Model Consent Form (consent page only, CMS's wording
// unchanged, blanks filled) goes out through Documenso; the signing link is texted from the office number. Once signed,
// the signed PDF + audit trail are kept FOREVER in the consent vault (and copied to the client's Signed Documents when
// the person is linked). A valid consent satisfies the Start enrollment gate; a revocation blocks both options until a
// new consent is signed. Revoke: admins + managers. Names / phones / emails go to the server in POST bodies only.
// The signing link is never shown here (it goes straight to the client's phone).
// =============================================================================

const detailOf = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return e?.message || 'Something went wrong';
};
const cs = () => healthsherpa.consent;

const styles = (c) => ({
  btn: (primary, disabled) => ({ padding: '6px 12px', fontSize: fs(13), borderRadius: 5, cursor: disabled ? 'default' : 'pointer',
    fontWeight: primary ? 600 : 400, opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
    border: primary ? '1px solid #185FA5' : `1px solid ${c.border}`, background: primary ? '#185FA5' : c.panel, color: primary ? '#fff' : c.text }),
  input: (w) => ({ padding: '6px 8px', fontSize: fs(13), borderRadius: 5, border: `1px solid ${c.border}`, background: c.panel, color: c.text,
    width: w || '100%', boxSizing: 'border-box' }),
  label: { fontSize: fs(11), fontWeight: 600, color: c.sub, marginBottom: 3, display: 'block' },
});

export const STATE_TONE = { cms_valid: '#2e7d32', revoked: '#A32D2D', expired: '#b9770e', none: '#6b7280', pending: '#1f5d99' };

// 5.40.0: what a consent gate means, in words
export function gateWords(g) {
  if (!g) return '';
  if (g.state === 'cms_valid') return `CMS consent signed ${g.consent.signed_label}, expires ${g.consent.expires_label}`;
  if (g.state === 'revoked') return `Consent revoked ${g.revoked.revoked_label} — send a new consent`;
  if (g.pending) return `CMS consent sent ${g.pending.sent_label} — waiting for the signature`;
  if (g.state === 'expired') return `CMS consent expired ${g.expired.expires_label}`;
  return 'No CMS consent on file';
}

export function ConsentChip({ consent, testid }) {
  // consent = a worklist row's {state, signed_label, expires_label, revoked_label, pending}
  if (!consent) return null;
  const st = consent.state === 'none' && consent.pending ? 'pending' : consent.state;
  const words = { cms_valid: `Consent ✓ to ${consent.expires_label}`, revoked: `Revoked ${consent.revoked_label}`,
    expired: `Consent expired ${consent.expires_label}`, pending: 'Consent sent', none: 'No consent' }[st];
  return (
    <span data-testid={testid || 'consent-chip'} data-state={st}
      style={{ display: 'inline-block', fontSize: fs(11), fontWeight: 700, color: '#fff', background: STATE_TONE[st] || '#6b7280',
        borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' }}>{words}</span>
  );
}

export async function openConsentFile(id, kind) {
  const r = await cs().file(id, kind);
  const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// --- send / resend ------------------------------------------------------------------------------------------------
export function ConsentSendDialog({ darkMode, target = {}, initial = {}, onClose, onSent }) {
  const c = usePersonColors(darkMode);
  const s = styles(c);
  const [meta, setMeta] = useState(null);
  const [name, setName] = useState(initial.consumer_name || '');
  const [phone, setPhone] = useState(initial.phone || '');
  const [email, setEmail] = useState(initial.email || '');
  const [language, setLanguage] = useState('en');
  const [agent, setAgent] = useState('henry');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  useEffect(() => { cs().meta().then((r) => { setMeta(r.data); setAgent(r.data.default_agent || 'henry'); }).catch((e) => setErr(detailOf(e))); }, []);
  const body = () => ({ consumer_name: name, phone, email: email || null, language, primary_agent: agent });
  const preview = async () => {
    setErr(null);
    try {
      const r = await cs().preview({ consumer_name: name, language, primary_agent: agent, phone, email: email || null });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setErr(e?.response?.status === 400 ? 'Type the name first' : detailOf(e)); }
  };
  const send = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await cs().send({ ...body(), person_id: target.person_id ?? null, client_id: target.client_id ?? null });
      setDone(r.data);
      if (onSent) onSent(r.data);
    } catch (e) { setErr(detailOf(e)); }
    setBusy(false);
  };
  const digits = phone.replace(/\D/g, '');
  const phoneOk = digits.length === 10 || (digits.length === 11 && digits[0] === '1');
  const segs = meta ? smsSegments(meta.text_template.replace('<link>', `https://sign.daoinsure.com/sign/${'x'.repeat(21)}`)) : null;
  return (
    <div role="dialog" aria-modal="true" aria-label="Send CMS consent form" data-testid="consent-send"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 12px', overflowY: 'auto' }}>
      <div style={{ background: c.bg, color: c.text, borderRadius: 10, width: 'min(560px, 100%)', padding: 18, display: 'grid', gap: 10, boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: fs(16), fontWeight: 700, flex: 1 }}>Send CMS consent form</div>
          <button style={s.btn(false)} onClick={onClose} disabled={busy}>Close</button>
        </div>
        <div style={{ fontSize: fs(12), color: c.sub }}>CMS Model Consent Form (consent page only). The client signs on their phone; the signed copy is kept forever.</div>
        {done ? (
          <div data-testid="consent-sent" style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontWeight: 700, color: '#2e7d32' }}>✓ Consent form created ({done.consent.language === 'es' ? 'Spanish' : 'English'}, {done.consent.agent_name})</div>
            <div role="status" data-testid="consent-sms" style={{ fontWeight: 600, color: done.sms.ok ? '#2e7d32' : '#c0392b' }}>
              {done.sms.ok ? '✓ Text sent to the client' : `✗ ${done.sms.message}`}
            </div>
            {!done.sms.ok && <div style={{ fontSize: fs(12), color: c.sub }}>The consent is on file as “sent”; fix the number and use Resend, or ask an admin.</div>}
            <div><button style={s.btn(true)} onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <>
            <div><label style={s.label} htmlFor="cs-name">Consumer name (as on the form)</label>
              <input id="cs-name" style={s.input()} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px' }}><label style={s.label} htmlFor="cs-phone">Mobile phone</label>
                <input id="cs-phone" style={s.input()} value={phone} maxLength={20} onChange={(e) => setPhone(e.target.value)} /></div>
              <div style={{ flex: '2 1 220px' }}><label style={s.label} htmlFor="cs-email">Email (optional)</label>
                <input id="cs-email" style={s.input()} value={email} maxLength={120} onChange={(e) => setEmail(e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div><label style={s.label} htmlFor="cs-lang">Language</label>
                <select id="cs-lang" style={s.input(130)} value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="en">English</option><option value="es">Spanish</option>
                </select></div>
              <div><label style={s.label} htmlFor="cs-agent">Primary writing agent</label>
                <select id="cs-agent" style={s.input(230)} value={agent} onChange={(e) => setAgent(e.target.value)}>
                  {(meta?.agents || [{ code: 'henry', label: 'Henry Dao' }, { code: 'harry', label: 'Harry Dao' }]).map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
                </select></div>
            </div>
            {meta && (
              <div style={{ fontSize: fs(12), color: c.sub }} data-testid="consent-text-preview">
                Text: “{meta.text_template.replace('<link>', '[link]')}” · {segs ? `${segs} segment${segs === 1 ? '' : 's'}` : ''}
              </div>
            )}
            {err && <div role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '6px 10px', borderRadius: 5, fontSize: fs(13) }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={s.btn(false, !name.trim())} disabled={!name.trim()} onClick={preview} data-testid="consent-preview">Preview PDF</button>
              <span style={{ flex: 1 }} />
              <button style={s.btn(true, busy || !name.trim() || !phoneOk)} disabled={busy || !name.trim() || !phoneOk} onClick={send} data-testid="consent-send-btn">
                {busy ? 'Sending…' : 'Send for signature by text'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// --- revoke ---------------------------------------------------------------------------------------------------------
export function ConsentRevokeDialog({ darkMode, consent, onClose, onDone }) {
  const c = usePersonColors(darkMode);
  const s = styles(c);
  const [how, setHow] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const go = async (confirmation) => {
    setBusy(true); setErr(null);
    try { const r = await cs().revoke(consent.id, { how, note, confirmation }); setDone(r.data); if (onDone) onDone(r.data); } catch (e) { setErr(detailOf(e)); }
    setBusy(false);
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="Revoke consent" data-testid="consent-revoke"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 12px' }}>
      <div style={{ background: c.bg, color: c.text, borderRadius: 10, width: 'min(520px, 100%)', padding: 18, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: fs(16), fontWeight: 700, flex: 1 }}>Revoke consent</div>
          <button style={s.btn(false)} onClick={onClose} disabled={busy}>Close</button>
        </div>
        {done ? (
          <div data-testid="consent-revoked">
            <div style={{ fontWeight: 700 }}>Consent revoked. Nothing was deleted; the form and history are kept.</div>
            {done.sms && <div style={{ color: done.sms.ok ? '#2e7d32' : '#c0392b' }}>{done.sms.ok ? '✓ Confirmation text sent' : `✗ ${done.sms.message}`}</div>}
            {!done.sms && <div style={{ color: c.sub }}>Logged: you'll email the written confirmation yourself.</div>}
            <div style={{ marginTop: 8 }}><button style={s.btn(true)} onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: fs(13) }}>Consent signed {consent.signed_label} by {consent.consumer_name}. Revoking blocks BOTH consent options until a new consent is signed.</div>
            <div><label style={s.label} htmlFor="cr-how">How did the client ask?</label>
              <select id="cr-how" style={s.input(200)} value={how} onChange={(e) => setHow(e.target.value)}>
                <option value="">Pick…</option><option value="email">Email</option><option value="phone">Phone</option><option value="other">Other</option>
              </select></div>
            <div><label style={s.label} htmlFor="cr-note">Note</label>
              <textarea id="cr-note" rows={3} maxLength={1000} style={s.input()} value={note} onChange={(e) => setNote(e.target.value)} /></div>
            {err && <div role="alert" style={{ color: '#c0392b', fontSize: fs(13) }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={s.btn(true, !how || busy)} disabled={!how || busy} onClick={() => go('sms')} data-testid="consent-revoke-sms">Revoke + send confirmation text</button>
              <button style={s.btn(false, !how || busy)} disabled={!how || busy} onClick={() => go('log_only')} data-testid="consent-revoke-log">Revoke — I'll email it myself (log only)</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// --- the panel (person profile) ----------------------------------------------------------------------------------------
export function ConsentPanel({ darkMode, personId, clientId, initial = {}, onChanged }) {
  const c = usePersonColors(darkMode);
  const s = styles(c);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [checkMsg, setCheckMsg] = useState(null);     // 5.40.2
  const load = useCallback(() => {
    try {
      cs().state({ person_id: personId ?? null, client_id: personId ? null : (clientId ?? null) })
        .then((r) => { setData(r.data); setErr(null); }).catch((e) => setErr(detailOf(e)));
    } catch (e) { setErr(detailOf(e)); }
  }, [personId, clientId]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); if (onChanged) onChanged(); };
  const g = data && data.gate;
  const valid = g && g.state === 'cms_valid' ? g.consent : null;
  const events = (data && data.events) || [];
  return (
    <div data-testid="consent-panel">
      {err && <div style={{ fontSize: fs(12), color: '#c0392b' }}>{err}</div>}
      {g && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span data-testid="consent-gate" data-state={g.state}
            style={{ fontSize: fs(13), fontWeight: 700, color: STATE_TONE[g.pending && g.state === 'none' ? 'pending' : g.state] }}>{gateWords(g)}</span>
          <span style={{ flex: 1 }} />
          <button style={s.btn(!valid)} onClick={() => setSendOpen(true)} data-testid="consent-send-open">{data.consents.length ? 'Resend consent' : 'Send CMS consent form'}</button>
          {g.pending && g.state !== 'cms_valid' && (
            <button style={s.btn(false)} data-testid="consent-check" onClick={async () => {
              setCheckMsg(null);
              try { const r = await cs().check(g.pending.id); setCheckMsg(r.data.message || r.data.result); load(); if (onChanged) onChanged(); } catch (e) { setCheckMsg(detailOf(e)); }
            }}>Check now</button>
          )}
          {valid && data.can_revoke && <button style={s.btn(false)} onClick={() => setRevoking(valid)} data-testid="consent-revoke-open">Revoke consent</button>}
        </div>
      )}
      {checkMsg && <div role="status" data-testid="consent-check-result" style={{ fontSize: fs(12), marginTop: 4 }}>{checkMsg}</div>}
      {data && data.consents.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {data.consents.map((k) => (
            <div key={k.id} style={{ fontSize: fs(12), padding: '3px 0', borderTop: `1px solid ${c.border}` }} data-testid="consent-row">
              <strong>{k.status}</strong> · sent {k.sent_label}{k.sent_by ? ` by ${k.sent_by}` : ''} · {k.language === 'es' ? 'Spanish' : 'English'} · {k.agent_name}
              {k.signed_label && ` · signed ${k.signed_label} · expires ${k.expires_label}`}
              {k.revoked_label && ` · revoked ${k.revoked_label}${k.revoked_by ? ` by ${k.revoked_by}` : ''}`}
              {' '}{['unsigned', 'signed', 'audit'].filter((kind) => k.files[kind]).map((kind) => (
                <button key={kind} onClick={() => openConsentFile(k.id, kind).catch(() => {})} data-testid={`consent-file-${kind}`}
                  style={{ background: 'none', border: 'none', color: '#4a8fd1', cursor: 'pointer', fontSize: fs(12), padding: '0 4px' }}>
                  {kind === 'unsigned' ? 'form' : kind === 'signed' ? 'signed PDF' : 'audit trail'}
                </button>
              ))}
            </div>
          ))}
          <button style={{ background: 'none', border: 'none', color: c.sub, cursor: 'pointer', fontSize: fs(12), padding: 0, marginTop: 4 }}
            onClick={() => setShowAll(!showAll)} data-testid="consent-history-toggle">{showAll ? 'Hide history' : `History (${events.length})`}</button>
          {showAll && events.map((e) => (
            <div key={e.id} style={{ fontSize: fs(11), color: c.sub }} data-testid="consent-event">{e.at_label} · {e.event}{e.by ? ` · ${e.by}` : ''}{e.detail ? ` — ${e.detail}` : ''}</div>
          ))}
        </div>
      )}
      {sendOpen && <ConsentSendDialog darkMode={darkMode} target={{ person_id: personId ?? null, client_id: personId ? null : (clientId ?? null) }}
        initial={initial} onClose={() => { setSendOpen(false); changed(); }} />}
      {revoking && <ConsentRevokeDialog darkMode={darkMode} consent={revoking} onClose={() => { setRevoking(null); changed(); }} />}
    </div>
  );
}
